import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import { TutorGenerationJobDTO, getJobStatus } from '../../../services/tutorQuizService';

interface Props {
  courseId: number;
  jobId: number;
  onComplete: () => void;
}

export default function TutorBatchJobProgress({ courseId, jobId, onComplete }: Props) {
  const [job, setJob] = useState<TutorGenerationJobDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let stopped = false;

    async function poll() {
      try {
        const data = await getJobStatus(courseId, jobId);
        if (stopped) return;
        setJob(data);
        if (data.status === 'DONE' || data.status === 'FAILED') {
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (data.status === 'DONE') onComplete();
        }
      } catch {
        if (!stopped) setError('Failed to fetch job status.');
      }
    }

    poll();
    intervalRef.current = setInterval(poll, 3000);

    return () => {
      stopped = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [courseId, jobId, onComplete]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!job) return <LinearProgress />;

  const total = job.totalTopics ?? 0;
  const done  = (job.completedTopics ?? 0) + (job.failedTopics ?? 0);
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {job.status === 'PENDING' && 'Queued — waiting to start…'}
        {job.status === 'RUNNING' && `Processing topics: ${done} / ${total}`}
        {job.status === 'DONE'    && `Complete — ${job.completedTopics} topic(s) processed.`}
        {job.status === 'FAILED'  && 'Generation job failed.'}
      </Typography>
      <LinearProgress
        variant={job.status === 'PENDING' ? 'indeterminate' : 'determinate'}
        value={pct}
        color={job.status === 'FAILED' ? 'error' : 'primary'}
      />
      {(job.failedTopics ?? 0) > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          {job.failedTopics} topic(s) failed to generate.
        </Typography>
      )}
    </Box>
  );
}
